import { downloadChatFile, type AttachedFile, type ChatFileRef, type Message, type TimelinePart, type ToolCallInfo } from "@/lib/api";
import { splitByPastes } from "@/lib/pastes";
import { formatBytes } from "@/lib/prompt-builder";
import { useStore } from "@/lib/store";
import { useState } from "react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ArtifactChip } from "../artifacts/ArtifactChip";

const ARTIFACT_TOOLS = new Set([
  "artifact_create",
  "artifact_write",
  "artifact_edit",
  "artifact_read",
  "artifact_delete",
]);

type MessageBubbleProps = {
  message: Message;
  isStreaming?: boolean;
  isLastMessage?: boolean;
  isLastUser?: boolean;
  onStop?: () => void;
  onReload?: () => void;
  onOpenArtifact?: (id: string) => void;
  mostRecentArtifactKey?: string;
  inlineArtifactExpanded?: boolean;
  onToggleInlineArtifact?: () => void;
};

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatFullDate(ts: number): string {
  return new Date(ts).toLocaleString([], {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

function ReasoningBlock({ reasoning, streaming, durationMs, effort }: { reasoning: string; streaming: boolean; durationMs?: number; effort?: "off" | "low" | "medium" | "high" }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-2 rounded-md border bg-muted/20 text-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted/40"
      >
        <i className={`fa-solid fa-chevron-right transition-transform ${expanded ? "rotate-90" : ""}`} />
        <i className={`fa-solid fa-brain text-primary ${streaming ? "fa-beat" : ""}`} />
        <span className="font-medium">{streaming ? "Thinking…" : "Thoughts"}</span>
        {effort && effort !== "off" && (
          <span className="text-[10px] text-muted-foreground capitalize">· {effort} effort</span>
        )}
        {durationMs !== undefined && !streaming && (
          <span className="text-[10px] text-muted-foreground tabular-nums">· {formatDuration(durationMs)}</span>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
          {reasoning.length}c
        </span>
      </button>
      {expanded && (
        <div className="border-t px-3 py-2">
          <div className="reasoning-markdown max-h-64 overflow-y-auto text-[11px] text-muted-foreground">
            <MarkdownRenderer content={reasoning} />
          </div>
        </div>
      )}
    </div>
  );
}

function ToolCallBlock({ tc }: { tc: ToolCallInfo }) {
  const [expanded, setExpanded] = useState(false);
  const resultStr = typeof tc.result === "string" ? tc.result : JSON.stringify(tc.result, null, 2);
  const argsStr = JSON.stringify(tc.args, null, 2);
  const shared = (tc.result as { shared?: ChatFileRef } | null)?.shared;

  return (
    <div className="rounded-md border bg-muted/30 text-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted/50"
      >
        <i className={`fa-solid fa-chevron-right transition-transform ${expanded ? "rotate-90" : ""}`} />
        <i className="fa-solid fa-wrench text-primary" />
        <span className="font-medium shrink-0">{tc.tool}</span>
        {typeof tc.args.command === "string" && (
          <code className="min-w-0 truncate font-mono text-muted-foreground">{tc.args.command.replace(/\s+/g, " ")}</code>
        )}
      </button>
      {shared?.type === "file" && (
        <div className="px-3 pb-2">
          <FileChip file={shared} />
        </div>
      )}
      {expanded && (
        <div className="border-t px-3 py-2 space-y-2">
          <div>
            <p className="text-[10px] text-muted-foreground font-semibold mb-0.5">Arguments</p>
            <pre className="text-[11px] text-muted-foreground overflow-x-auto whitespace-pre-wrap">{argsStr}</pre>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground font-semibold mb-0.5">Result</p>
            <pre className="text-[11px] text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">{resultStr}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

type ArtifactContext = {
  onOpenArtifact?: (id: string) => void;
  mostRecentArtifactKey?: string;
  inlineArtifactExpanded?: boolean;
  onToggleInlineArtifact?: () => void;
};

function ToolCallView({ tc, artifactKey, artifacts }: { tc: ToolCallInfo; artifactKey: string; artifacts: ArtifactContext }) {
  if (ARTIFACT_TOOLS.has(tc.tool) && artifacts.onOpenArtifact) {
    const isMostRecent = artifacts.mostRecentArtifactKey === artifactKey;
    return (
      <ArtifactChip
        tc={tc}
        onOpen={artifacts.onOpenArtifact}
        inlineExpandable={isMostRecent}
        expanded={isMostRecent && !!artifacts.inlineArtifactExpanded}
        onToggleInline={isMostRecent ? artifacts.onToggleInlineArtifact : undefined}
      />
    );
  }
  return <ToolCallBlock tc={tc} />;
}

function Timeline({ messageId, parts, streaming, reasoningDurationMs, effort, artifacts }: {
  messageId: string;
  parts: TimelinePart[];
  streaming: boolean;
  reasoningDurationMs?: number;
  effort?: "off" | "low" | "medium" | "high";
  artifacts: ArtifactContext;
}) {
  let lastReasoningIdx = -1;
  parts.forEach((p, i) => { if (p.kind === "reasoning") lastReasoningIdx = i; });
  let toolIndex = 0;

  return (
    <div className="flex flex-col gap-1.5">
      {parts.map((part, i) => {
        const isLast = i === parts.length - 1;
        if (part.kind === "reasoning") {
          return (
            <ReasoningBlock
              key={i}
              reasoning={part.text}
              streaming={streaming && isLast}
              durationMs={i === lastReasoningIdx ? reasoningDurationMs : undefined}
              effort={effort}
            />
          );
        }
        if (part.kind === "content") {
          return <MarkdownRenderer key={i} content={part.text} />;
        }
        const artifactKey = `${messageId}:${toolIndex++}`;
        return <ToolCallView key={i} tc={part.call} artifactKey={artifactKey} artifacts={artifacts} />;
      })}
    </div>
  );
}

function UserContent({ message }: { message: Message }) {
  return (
    <>
      {splitByPastes(message.content, message.files).map((segment, i) =>
        segment.kind === "text" ? (
          segment.text.trim() && <MarkdownRenderer key={i} content={segment.text} />
        ) : (
          <details key={i} className="my-1.5 rounded-md bg-foreground/10 text-xs">
            <summary className="cursor-pointer select-none px-2 py-1">
              <i className="fa-solid fa-clipboard mr-1.5" />
              {segment.token.slice(1, -1)}
            </summary>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words px-2 pb-2 font-mono text-xs">{segment.paste.content}</pre>
          </details>
        )
      )}
    </>
  );
}

function FileChip({ file }: { file: Exclude<AttachedFile, { type: "image" | "paste" }> }) {
  const chatId = useStore((s) => s.activeId);
  const label = (
    <>
      <i className="fa-solid fa-file-lines text-[10px]" />
      {file.name}
      {file.type === "file" && <span className="text-muted-foreground">{formatBytes(file.size)}</span>}
    </>
  );
  const chipClass = "inline-flex items-center gap-1 rounded-md bg-foreground/10 px-2 py-0.5 text-xs";
  if (file.type !== "file" || !file.id || !chatId) return <span className={chipClass}>{label}</span>;
  return (
    <button
      type="button"
      title="Download"
      className={`${chipClass} hover:bg-foreground/20`}
      onClick={() => downloadChatFile(chatId, file.id, file.name).catch((e) => console.warn(e))}
    >
      {label}
    </button>
  );
}

export function MessageBubble({ message, isStreaming, isLastMessage, isLastUser, onStop, onReload, onOpenArtifact, mostRecentArtifactKey, inlineArtifactExpanded, onToggleInlineArtifact }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isWaiting = !isUser && isLastMessage && isStreaming && !message.content && !message.toolCalls?.length && !message.reasoning && !message.error;

  if (isUser) {
    const attachments = message.files?.filter((f) => f.type !== "paste") ?? [];
    return (
      <div>
        <div className="flex justify-end">
          <div className="max-w-[85%] text-[12px] rounded-2xl bg-muted mt-4 px-4 py-2.5 text-foreground sm:max-w-[75%]">
            {message.content && (
              <div className="user-markdown text-sm sm:text-[15px]">
                <UserContent message={message} />
              </div>
            )}
            {attachments.length > 0 && (
              <div className={`flex flex-wrap gap-1.5 ${message.content ? "mt-2" : ""}`}>
                {attachments.map((f) =>
                  f.type === "image" ? (
                    <img key={f.name} src={f.dataUrl} alt={f.name} className="max-w-48 max-h-48 rounded-md" />
                  ) : (
                    <FileChip key={f.name} file={f} />
                  )
                )}
              </div>
            )}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-end gap-3 px-1 text-[11px] text-muted-foreground">
          {message.sentAt && <span title={formatFullDate(message.sentAt)}>{formatTime(message.sentAt)}</span>}
          {isLastUser && isStreaming && (
            <button onClick={onStop} className="hover:text-destructive" title="Stop generating">
              <i className="fa-solid fa-stop" />
            </button>
          )}
          {isLastUser && !isStreaming && (
            <button onClick={onReload} className="hover:text-foreground" title="Regenerate">
              <i className="fa-solid fa-arrow-rotate-right" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const trimmedReasoning = message.reasoning?.trim() ?? "";
  const streamingActive = !!isStreaming && !!isLastMessage;
  const reasoningStreaming = streamingActive && !message.content && !!trimmedReasoning;
  const timeline = message.timeline;
  const hasTimeline = !!timeline && timeline.length > 0;
  const artifactCtx: ArtifactContext = { onOpenArtifact, mostRecentArtifactKey, inlineArtifactExpanded, onToggleInlineArtifact };

  return (
    <div>
      {hasTimeline ? (
        <div className="mb-2">
          <Timeline
            messageId={message.id}
            parts={timeline!}
            streaming={streamingActive && !message.activeToolCall}
            reasoningDurationMs={message.stats?.reasoningDurationMs}
            effort={message.stats?.effort}
            artifacts={artifactCtx}
          />
        </div>
      ) : (
        <>
          {trimmedReasoning && (
            <ReasoningBlock reasoning={trimmedReasoning} streaming={reasoningStreaming} durationMs={message.stats?.reasoningDurationMs} effort={message.stats?.effort} />
          )}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mb-2 flex flex-col gap-1.5">
              {message.toolCalls.map((tc, i) => (
                <ToolCallView key={i} tc={tc} artifactKey={`${message.id}:${i}`} artifacts={artifactCtx} />
              ))}
            </div>
          )}
        </>
      )}
      {message.activeToolCall && (
        <div className="flex items-center gap-2 py-1.5 text-sm text-muted-foreground">
          <i className="fa-solid fa-spinner-third fa-spin" />
          <i className="fa-solid fa-wrench text-primary" />
          <span>Calling <span className="font-medium">{message.activeToolCall}</span>...</span>
          {!!message.activeToolCallChars && (
            <span className="text-xs tabular-nums opacity-70">{message.activeToolCallChars}c</span>
          )}
        </div>
      )}
      {isWaiting && (
        <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
          <i className="fa-solid fa-spinner-third fa-spin" />
          {message.promptProgress != null ? (
            <div className="flex items-center gap-2 flex-1 max-w-48">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${Math.round(message.promptProgress * 100)}%` }}
                />
              </div>
              <span className="text-xs tabular-nums">{Math.round(message.promptProgress * 100)}%</span>
            </div>
          ) : (
            "Processing…"
          )}
        </div>
      )}
      {!hasTimeline && message.content && <MarkdownRenderer content={message.content} />}
      {message.error && (
        <div role="alert" className="mt-2 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <i className="fa-solid fa-triangle-exclamation mt-0.5" />
          <p className="flex-1 min-w-0 whitespace-pre-wrap break-words">{message.error}</p>
          {isLastMessage && !isStreaming && onReload && (
            <button onClick={onReload} className="shrink-0 rounded px-2 py-0.5 text-xs font-medium hover:bg-destructive/15">
              Retry
            </button>
          )}
        </div>
      )}
      {message.stats && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          {message.sentAt && (
            <span title={formatFullDate(message.sentAt)}><i className="fa-solid fa-clock-rotate-left mr-1" />{formatTime(message.sentAt)}</span>
          )}
          <span><i className="fa-solid fa-microchip mr-1" />{message.stats.model}</span>
          <span title="Tokens in prompt sent to model"><i className="fa-solid fa-arrow-right-to-arc mr-1" />{message.stats.promptTokens} prompt</span>
          <span title="Tokens generated"><i className="fa-solid fa-arrow-left-from-arc mr-1" />{message.stats.completionTokens} completion</span>
          {message.stats.contextLength != null && (
            <span title={`${message.stats.promptTokens} / ${message.stats.contextLength} context tokens`}>
              <i className="fa-solid fa-gauge mr-1" />
              {Math.round((message.stats.promptTokens / message.stats.contextLength) * 100)}% ctx
            </span>
          )}
          <span><i className="fa-solid fa-gauge-high mr-1" />{message.stats.tokensPerSecond.toFixed(1)} tok/s</span>
          <span><i className="fa-solid fa-hourglass-start mr-1" />{(message.stats.timeToFirstToken / 1000).toFixed(2)}s prompt</span>
          <span><i className="fa-solid fa-clock mr-1" />{(message.stats.durationMs / 1000).toFixed(1)}s total</span>
        </div>
      )}
      {!message.stats && message.content && message.sentAt && !isStreaming && (
        <div className="mt-1 text-[11px] text-muted-foreground">
          <span title={formatFullDate(message.sentAt)}>{formatTime(message.sentAt)}</span>
        </div>
      )}
    </div>
  );
}
